import React from 'react';

import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import styled from 'styled-components';

import { Button } from '../../components/Button';
import { HStack, VStack } from '../../components/Common';
import { Input } from '../../components/Input';
import { useGetPublicApplicationVersions } from '../../services/application/queries';
import { useLogin } from '../../services/login/queries';
import { usePostPdsPassword } from '../../services/pds/queries';
import { setToken } from '../../utils/handleToken';

import type { PostLoginReq } from '../../services/login';

const INITIAL_VALUES: PostLoginReq = {
	loginId: '',
	password: '',
	accountType: 'ADMIN_USER',
	isNotification: false,
	isKdShop: false,
};

const Login = () => {
	const navigate = useNavigate();
	const [formValues, setFormValues] = React.useState(INITIAL_VALUES);
	const [isKiosk, setIsKiosk] = React.useState(false);
	const { mutate: loginMutate } = useLogin();
	const { mutate: pdsMutate } = usePostPdsPassword();
	const { data: appVersions } = useGetPublicApplicationVersions('KIOSK_OLD');

	React.useEffect(() => {
		const userAgent = navigator.userAgent;
		if (userAgent && userAgent.includes('HS101')) {
			setIsKiosk(true);
		} else {
			setIsKiosk(false);
		}
	}, []);

	const onChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
		const { name, value } = e.target;
		setFormValues((prev) => ({
			...prev,
			[name]: value,
		}));
	};

	const handleDownload = async () => {
		if (!appVersions || appVersions.length === 0) {
			toast.error('다운로드할 버전 정보가 없습니다.');
			return;
		}

		const downloadUrl = appVersions[0].url;
		if (!downloadUrl) {
			toast.error('다운로드 URL이 없습니다.');
			return;
		}

		try {
			const response = await fetch(downloadUrl);
			if (!response.ok) throw new Error('다운로드 실패');

			const blob = await response.blob();
			const url = window.URL.createObjectURL(blob);
			const a = document.createElement('a');
			a.href = url;

			const urlPath = downloadUrl.split('/');
			const fileName = urlPath[urlPath.length - 1] || 'download';
			a.download = fileName;

			document.body.appendChild(a);
			a.click();
			document.body.removeChild(a);
			window.URL.revokeObjectURL(url);

			toast.success('다운로드가 시작되었습니다.');
		} catch (error) {
			console.error('Download error:', error);
			toast.error('다운로드 중 오류가 발생했습니다.');
		}
	};

	const onSubmit = (e: React.FormEvent<HTMLFormElement>) => {
		e.preventDefault();
		loginMutate(
			{
				...formValues,
				isNotification: false,
				isKdShop: false,
			},
			{
				onSuccess: () => {
					pdsMutate(
						{ password: process.env.REACT_APP_PASSWORD ?? '' },
						{
							onSuccess: (res) => {
								setToken(res.token);
								setFormValues(INITIAL_VALUES);
								navigate('/', { relative: 'path' });
							},
							onError: (err) => {
								toast.error(err.response?.data.message);
							},
						},
					);
				},
				onError: (err) => {
					toast.error(err.response?.data.message);
				},
			},
		);
	};

	return (
		<Wrapper $alignItems="center" $justifyContent="center" $gap="2rem">
			<div className="logo">
				<img src={'/assets/logo/logo.png'} alt="logo" />
				<span>굿팜</span>
				<span>다운로더</span>
			</div>
			<form onSubmit={onSubmit}>
				<FormStack>
					<Input name="loginId" onChange={onChange} value={formValues.loginId} placeholder="아이디를 입력해주세요." />
					<Input
						name="password"
						onChange={onChange}
						value={formValues.password}
						type="password"
						placeholder="비밀번호를 입력해주세요."
					/>
					<StyledButton type="submit">로그인</StyledButton>
					{isKiosk && (
						<KioskButton type="button" onClick={handleDownload}>
							<img src="/gear.svg" alt="download" />
							<span>키오스크 업데이트</span>
						</KioskButton>
					)}
				</FormStack>
			</form>
		</Wrapper>
	);
};

const Wrapper = styled(VStack)`
	height: 100vh;

	.logo {
		display: flex;
		align-items: center;
		width: 100%;
		justify-content: center;
		font-size: 40px;
		font-weight: bold;
		font-family: monospace;
		flex-direction: row;
		img {
			width: calc(5rem + 3vw);
			height: calc(5rem + 3vw);
			padding: 0;
			font-size: 0;
			margin: 0;
		}
		span {
			&:nth-child(2) {
				font-size: calc(2.4rem + 2.5vw);
				color: ${({ theme }) => theme.colors.primary[600]};
			}
			&:nth-child(3) {
				font-size: calc(2.4rem + 2.5vw);
			}
		}
	}
`;

const FormStack = styled.div`
	display: flex;
	flex-direction: column;
	gap: 1.2rem;
	width: 320px;
	margin: 0 auto;
`;

const StyledButton = styled(Button)`
	width: 100%;
	height: 48px;
	font-size: 1.1rem;
	border-radius: 8px;
	font-weight: bold;
	margin-top: 0.5rem;
`;

const KioskButton = styled.button`
	width: 176px;
	height: 56px;
	border: 1px solid #3ecec7cc;
	background: white;
	display: flex;
	align-items: center;
	justify-content: center;
	gap: 12px;
	border-radius: 8px;
	cursor: pointer;
	margin: 0 auto;
	margin-top: 30px;
	transition: background-color 0.2s;

	&:hover {
		background: #effdfd;
	}

	img {
		width: 24px;
		height: 24px;
	}

	span {
		font-weight: 700;
		font-size: 15px;
		color: #17a7a1;
	}
`;
export default Login;
